import { Injectable, NgZone } from '@angular/core';
import { CoreAnalytics } from '@core-modules/analytics/providers/core-analytics';
import { DeviceProvider } from '@core-modules/providers/device/device';
import { EnvironmentProvider } from '@core-modules/providers/environment-variables/environment';
import { DevereCapacitorIntercom } from 'devere-capacitor-intercom';
import { BehaviorSubject, interval, Subscription } from 'rxjs';

/*
  Generated class for the IntercomSupport provider.

  See https://angular.io/docs/ts/latest/guide/dependency-injection.html
  for more info on providers and Angular DI.
*/

@Injectable()
export class IntercomSupportProvider
{
    //#region Constructor

    /**
     * Class constructor
     * @param device 
     * @param analytics 
     * @param zone 
     */
    constructor
        (
            private device: DeviceProvider,
            private analytics: CoreAnalytics,
            private zone: NgZone,
            private env: EnvironmentProvider
        )
    {
        this.unreadMessagesCount = new BehaviorSubject<number>(0);
    }

    //#endregion


    //#region Properties

    /**
     * Number of unread messages
     */
    public unreadMessagesCount: BehaviorSubject<number>;

    /**
     * Subscription to the message notifications
     */
    private messagesSubscription: Subscription;

    //#endregion


    //#region Methods

    /**
     * Initialize the Intercom Support plugin
     * @param clientHash 
     * @param userFullName 
     * @param userEmailAddress 
     * @param intercomUserHmac 
     */
    public async initializeIntercomSupport(clientHash: string, userFullName: string, userEmailAddress: string, intercomUserHmac: string)
    {
       // Initialize the plugin using the App Id and Api Key. The userHmac variable here is used
        // to initialize the web version of the plugin.
        await DevereCapacitorIntercom.initializePlugin({ appId: this.env.intercom.appId, apiKey: this.getIntercomApiKey() });

        // Reset Intercom
        await this.resetClient();

        // Set the user's HMAC before initializing the plugin to make sure all further requests are
        // coming from a genuine source. Will only apply in cases of iOS and Android
        await DevereCapacitorIntercom.setUserHash({ hmac: intercomUserHmac });

        // Register the client for support as an identified user
        await DevereCapacitorIntercom.registerIdentifiedUser({ userId: clientHash, email: (this.env.production) ? userEmailAddress : this.env.customerSupportEmailAddress, name: userFullName });

        // Set the unread messages check subscrption
        this.subscribeMessagesCheck();
    }

    /**
     * Initialize the Intercom plugin
     */
    private getIntercomApiKey(): string
    {
        if (this.device.isBrowser())
        {
            // If the app is running within a browser then return null
            // as Api Keys are not used in this environment.
            return null;
        }
        else if (this.device.isAndroid())
        {
            // If the app is running on an Android device then return the
            // Android API key.
            return this.env.intercom.apiKeyAndroid;
        }
        else if (this.device.isiOS())
        {
            // If the app is running on an Android device then return the
            // iOS API key.
            return this.env.intercom.apiKeyiOS;
        }
        else
        {
            // Sanity check: should never happen
            throw `Intercom API Key for platform ${this.device.platform.platforms().join(',')} could not be found!`
        }
    }

    /**
    * Show Intercom's conversations
    */
    public async showConversations()
    {
        // update the user's custom properties
        this.analytics.TrackEvent('contacted_support', { supportChannel: 'chat' });
        await DevereCapacitorIntercom.displayMessageComposer({ message: '' });
    }

    /**
     * Get the number of unread messages for this user
     */
    private async getUnreadMessages(): Promise<number>
    {
        // interact with the intercomp plugin to retrieve
        // the number of unread messages
        let unreadCount = await DevereCapacitorIntercom.unreadMessageCount();
        return unreadCount.value;
    }

    /**
     * Initiate a process to check if the user has any messages every minute
     */
    private subscribeMessagesCheck(): void
    {
        /**
         * Set an interval where with every minute
         * the application will perform a check on
         * the unread messages count and emit the event
         * through the unreadMessagesCount observable
         */
        this.messagesSubscription = interval(60 * 1000)
            .subscribe(async () =>
            {
                let count = await this.getUnreadMessages();
                this.zone.run(() =>
                {
                    this.unreadMessagesCount.next(count);
                });
            });
    }

    /**
     * Reset the client's cache and identity
     */
    public async resetClient(): Promise<any>
    {
        await DevereCapacitorIntercom.logout();

        // If there is a message subscription then unsubscribe from it
        if (this.messagesSubscription)
        {
            this.messagesSubscription.unsubscribe();
        }
    }


    //#endregion
}
